<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { getStoredAuth } from '@/services/auth.credentials'
import { useDronecastStore } from '@/stores/dronecastStore'
import { DronecastClient } from '@/services/DronecastClient'
import { DronecastPlayer } from '@/sdk'
import type { ConnectionState } from '@/sdk/core/errorMessages'
import type { EndpointStats, VideoSourceType } from '@dronecast/shared'
import { isQosMetricSupported } from '@dronecast/shared'

const dronecastStore = useDronecastStore()

// 상태
const errorMessage = ref<string>("")

// 미니 플레이어 열림 상태 (index 기반)
const openPlayers = ref<Set<number>>(new Set())
// 플레이어 ref (index → DronecastPlayer 인스턴스)
const playerRefs = ref<Map<number, InstanceType<typeof DronecastPlayer>>>(new Map())
// 플레이어 연결 상태 (index → ConnectionState)
const playerStates = ref<Map<number, ConnectionState>>(new Map())
// 미리보기 모드에서 실시간 캐스터 링크 상태 (index → status)
const casterLinkStates = ref<Map<number, string>>(new Map())
// 서버 push 미디어 통계 (castroomKey → EndpointStats)
const castroomStats = ref<Map<string, EndpointStats>>(new Map())
// 상세 통계 펼침 상태 (castroomKey 기반)
const expandedStats = ref<Set<string>>(new Set())

// 활성 녹화 — castroomKey → { recordingId, status, startedAt }
interface ActiveRecordingState {
  recordingId: string
  status: 'starting' | 'recording' | 'stopping' | 'completed' | 'failed' | 'orphan'
  startedAt: number
}
const activeRecordings = ref<Map<string, ActiveRecordingState>>(new Map())

// 캐스트룸별 녹화 정책 캐시 — castroomKey → policy(또는 null)
interface ClientRecordingPolicy {
  castroomId: { gcs: string; drone: string; camera: string }
  trigger: 'manual' | 'on_casting_start' | 'on_first_viewer'
  format?: 'webm' | 'mp4'
  enabled: boolean
  updatedAt: number
}
const recordingPolicies = ref<Map<string, ClientRecordingPolicy | null>>(new Map())

// 정책 모달 상태
const policyModal = ref<{ open: boolean; castroom: any | null; draft: ClientRecordingPolicy | null; busy: boolean; error: string }>({
  open: false,
  castroom: null,
  draft: null,
  busy: false,
  error: '',
})

const setPlayerRef = (index: number, el: any) => {
  if (el) playerRefs.value.set(index, el)
  else playerRefs.value.delete(index)
}

const onPlayerStateChanged = (index: number, state: ConnectionState) => {
  playerStates.value = new Map(playerStates.value.set(index, state))
}

const onCasterLinkStatusChanged = (index: number, status: string) => {
  casterLinkStates.value = new Map(casterLinkStates.value.set(index, status))
}

const castroomKey = (castroom: any) => {
  const id = castroom.castingSource.castroomId
  return `${id.gcs}:${id.drone}:${id.camera}`
}

/** 썸네일 URL — updatedAt을 쿼리로 붙여 캐시 버스팅 */
const thumbUrl = (castroom: any): string | null => {
  if (!castroom.thumbnailUrl) return null
  return castroom.thumbnailUpdatedAt
    ? `${castroom.thumbnailUrl}?t=${castroom.thumbnailUpdatedAt}`
    : castroom.thumbnailUrl
}

const toggleStats = (key: string) => {
  if (expandedStats.value.has(key)) expandedStats.value.delete(key)
  else expandedStats.value.add(key)
  expandedStats.value = new Set(expandedStats.value)
}

const getStats = (castroom: any): EndpointStats | undefined => {
  return castroomStats.value.get(castroomKey(castroom))
}

// ── Stats 표시 유틸 ──

type StatsLevel = 'good' | 'warn' | 'bad'

function statsLevel(value: number | null | undefined, warnThreshold: number, badThreshold: number, higher = true): StatsLevel {
  if (value == null) return 'good'
  if (higher) return value >= badThreshold ? 'bad' : value >= warnThreshold ? 'warn' : 'good'
  return value <= badThreshold ? 'bad' : value <= warnThreshold ? 'warn' : 'good'
}

function statsColor(level: StatsLevel): string {
  switch (level) {
    case 'good': return '#28a745'
    case 'warn': return '#ffc107'
    case 'bad':  return '#dc3545'
  }
}

function formatVal(v: number | null | undefined, unit: string, decimals = 0): string {
  if (v == null) return '-'
  return `${decimals > 0 ? v.toFixed(decimals) : v}${unit}`
}

// 핵심 지표 정의 (서버 push EndpointStats 기준)
interface MetricDef {
  key: string
  label: string
  unit: string
  warn: number
  bad: number
  higher: boolean
  noColor?: boolean
  tooltip: string
}

const primaryMetrics: MetricDef[] = [
  { key: 'inboundBitrateKbps', label: 'Bitrate',  unit: 'kbps', warn: 500, bad: 200, higher: false,
    tooltip: '수신 비트레이트. 네트워크 대역폭 부족이나 송출 품질 저하 시 하락. 200kbps 이하면 심각한 품질 저하' },
  { key: 'inboundFps',         label: 'FPS',      unit: '',     warn: 20,  bad: 10,  higher: false,
    tooltip: '수신 FPS. 30fps 송출 시 25~30 정상. 10 이하면 영상이 뚝뚝 끊김' },
  { key: 'inboundPacketLossRate', label: 'Loss',   unit: '%',    warn: 2,   bad: 5,   higher: true,
    tooltip: '패킷 손실률. 1~2%는 체감 어려움, 5% 이상이면 화면 깨짐 발생, 10% 이상이면 시청 불가' },
  { key: 'rttMs',              label: 'RTT',       unit: 'ms',   warn: 100, bad: 300, higher: true,
    tooltip: 'Round Trip Time — 미디어 서버까지 패킷 왕복 시간. 현재 릴레이는 보고하지 않는다' },
  { key: 'jitterMs',           label: 'Jitter',    unit: 'ms',   warn: 30,  bad: 100, higher: true,
    tooltip: '수신 패킷 도착 간격의 변동폭. 30ms 이하 양호, 100ms 이상 문제. 현재 릴레이는 보고하지 않는다' },
]

// 상세 지표 정의
const detailMetrics: MetricDef[] = [
  { key: 'outboundBitrateKbps', label: 'Out Bitrate', unit: 'kbps', warn: 500, bad: 200, higher: false,
    tooltip: '송신 비트레이트. 서버에서 viewer들에게 전달하는 대역폭' },
  { key: 'resolution',         label: '해상도',     unit: '',     warn: 0,   bad: 0,   higher: true, noColor: true,
    tooltip: '수신 중인 영상 해상도. 현재 릴레이는 보고하지 않는다' },
  { key: 'codec',              label: '코덱',       unit: '',     warn: 0,   bad: 0,   higher: true, noColor: true,
    tooltip: '사용 중인 비디오 코덱. 현재 릴레이는 보고하지 않는다' },
  { key: 'nackCount',          label: 'NACK',       unit: '',     warn: 10,  bad: 50,  higher: true,
    tooltip: 'NACK 수신 횟수. 손실 패킷 재전송 요청. 현재 릴레이는 보고하지 않는다' },
  { key: 'pliCount',           label: 'PLI',        unit: '',     warn: 5,   bad: 20,  higher: true,
    tooltip: 'PLI 수신 횟수. 키프레임 재요청. 현재 릴레이는 보고하지 않는다' },
  { key: 'receiverCount',      label: '수신자',     unit: '',     warn: 0,   bad: 0,   higher: true, noColor: true,
    tooltip: '현재 연결된 viewer 수' },
]

function getMetricValue(stats: EndpointStats, key: string): any {
  return (stats as any)[key] ?? null
}

function getMetricLevel(metric: MetricDef, value: any): StatsLevel {
  if (metric.noColor) return 'good'
  if (typeof value !== 'number') return 'good'
  return statsLevel(value, metric.warn, metric.bad, metric.higher)
}

/**
 * 지표 표시 텍스트 — 구조적 미지원은 'N/A', 수집 전/미보고는 '-'.
 * shared/qos-support.ts의 매트릭스로 sourceType별 지원 여부 판정.
 */
function renderMetric(metric: MetricDef, stats: EndpointStats, sourceType?: VideoSourceType): string {
  if (!isQosMetricSupported(sourceType, metric.key)) return 'N/A'
  const value = getMetricValue(stats, metric.key)
  return formatVal(value, metric.unit)
}

function renderMetricLevel(metric: MetricDef, stats: EndpointStats, sourceType?: VideoSourceType): StatsLevel {
  if (!isQosMetricSupported(sourceType, metric.key)) return 'good'  // 색상 없음
  return getMetricLevel(metric, getMetricValue(stats, metric.key))
}

function metricTooltip(metric: MetricDef, sourceType?: VideoSourceType): string {
  if (!isQosMetricSupported(sourceType, metric.key)) {
    return `${metric.label} — 현재 입력 유형에서 제공하지 않는 지표 (N/A)`
  }
  return metric.tooltip
}

const linkStatusColor = (status?: string) => {
  switch (status) {
    case 'connected': return '#28a745'
    case 'unstable': return '#ffc107'
    case 'disconnected': return '#dc3545'
    default: return '#6c757d'
  }
}

const linkStatusLabel = (status?: string) => {
  switch (status) {
    case 'connected': return '원활'
    case 'unstable': return '불안정'
    case 'disconnected': return '끊김'
    default: return '미확인'
  }
}

/**
 * 영상 소스 입력 유형 약어 표시 (DES_CMN_영상소스정보조회_입력유형별분석.md §10)
 * sourceType 미지정 시 rtspUrl 유무로 폴백 표시 (구버전 caster 호환).
 */
const sourceTypeLabel = (sourceType?: string, rtspUrl?: string | null) => {
  switch (sourceType) {
    case 'browser_camera': return 'WEB-CAM'
    case 'browser_screen': return 'WEB-SCR'
    case 'opencv_device': return 'CV-DEV'
    case 'opencv_rtsp': return 'CV-RTSP'
    case 'gst_rtsp': return 'GST-RTSP'
    default: return rtspUrl ? 'RTSP' : '-'
  }
}

const sourceTypeTitle = (sourceType?: string) => {
  switch (sourceType) {
    case 'browser_camera': return '브라우저 getUserMedia (웹캠/USB)'
    case 'browser_screen': return '브라우저 getDisplayMedia (화면 공유)'
    case 'opencv_device': return 'Python SDK OpenCV 로컬 비디오 디바이스'
    case 'opencv_rtsp': return 'Python SDK OpenCV RTSP 인입'
    case 'gst_rtsp': return 'GStreamer Caster 서버사이드 RTSP (Headless)'
    default: return '소스 유형 미지정'
  }
}

const playerLinkStatus = (index: number) => {
  const state = playerStates.value.get(index)
  if (state === 'playing') return 'connected'
  if (state === 'buffering' || state === 'subscribing' || state === 'joining' || state === 'connecting') return 'unknown'
  if (state === 'error') return 'disconnected'
  return 'unknown'
}

const isPlayerRetryable = (index: number) => {
  const state = playerStates.value.get(index)
  return state === 'stopped' || state === 'error'
}

const retryPlayer = (index: number) => {
  const player = playerRefs.value.get(index)
  if (player) {
    const castroom = castroomList.value[index]
    player.play(castroom.castingSource.castroomId)
  }
}

const openViewerPopup = (index: number) => {
  const castroom = castroomList.value[index]
  if (!castroom) return
  const { gcs, drone, camera } = castroom.castingSource.castroomId
  const params = new URLSearchParams({ gcs, drone, camera })
  const target = `popup_view_${gcs}_${drone}_${camera}`
  window.open(`/service/play?${params}`, target, 'width=800,height=600')
}

const togglePlayer = (index: number) => {
  if (openPlayers.value.has(index)) {
    openPlayers.value.delete(index)
    playerStates.value.delete(index)
    casterLinkStates.value.delete(index)
  } else {
    openPlayers.value.add(index)
  }
  // reactivity trigger
  openPlayers.value = new Set(openPlayers.value)
}

const castroomList = computed(() => dronecastStore.castroomList)

let dronecastClient: DronecastClient | null = null
let adminWs: WebSocket | null = null

const onClickFetchDronecastList = () => {
  dronecastStore.fetchAllCastrooms()
}

// ── 녹화 컨트롤 (BL-638) ──────────────────────────

function sendAdminMessage(type: string, payload: any) {
  if (!adminWs || adminWs.readyState !== WebSocket.OPEN) {
    errorMessage.value = 'Admin WebSocket이 연결되지 않았습니다.'
    setTimeout(() => { errorMessage.value = '' }, 3000)
    return
  }
  adminWs.send(JSON.stringify({ protocol: 'dronecast/1.0', type, payload }))
}

const recordingState = (castroom: any): ActiveRecordingState | undefined => {
  return activeRecordings.value.get(castroomKey(castroom))
}

const isRecording = (castroom: any): boolean => {
  const s = recordingState(castroom)
  return !!s && (s.status === 'recording' || s.status === 'starting' || s.status === 'stopping')
}

const onClickStartRecording = (castroom: any) => {
  if (isRecording(castroom)) return
  sendAdminMessage('cmd.recording.start', {
    castroomId: castroom.castingSource.castroomId,
    // format/trigger는 서버 기본값(webm/manual) 사용
  })
}

const onClickStopRecording = (castroom: any) => {
  const state = recordingState(castroom)
  if (!state) return
  sendAdminMessage('cmd.recording.stop', { recordingId: state.recordingId })
}

// ── 녹화 정책 (BL-637) ──────────────────────

const policyOf = (castroom: any): ClientRecordingPolicy | null | undefined => {
  return recordingPolicies.value.get(castroomKey(castroom))
}

const policyLabel = (castroom: any): string => {
  const p = policyOf(castroom)
  if (p === undefined) return ''  // 미조회
  if (!p || !p.enabled || p.trigger === 'manual') return '수동'
  if (p.trigger === 'on_casting_start') return '송출 시작 시'
  if (p.trigger === 'on_first_viewer') return '첫 시청자 시'
  return p.trigger
}

const openPolicyModal = (castroom: any) => {
  const id = castroom.castingSource.castroomId
  policyModal.value = {
    open: true,
    castroom,
    draft: {
      castroomId: { gcs: id.gcs, drone: id.drone, camera: id.camera },
      trigger: 'manual',
      format: 'webm',
      enabled: false,
      updatedAt: Date.now(),
    },
    busy: false,
    error: '',
  }
  // 서버에서 현재 정책 조회 — onmessage 핸들러에서 draft 갱신
  sendAdminMessage('cmd.recording.get_policy', { castroomId: id })
}

const closePolicyModal = () => {
  policyModal.value = { open: false, castroom: null, draft: null, busy: false, error: '' }
}

const savePolicyModal = () => {
  if (!policyModal.value.draft) return
  policyModal.value.busy = true
  policyModal.value.error = ''
  // updatedAt은 서버에서 채움 (PolicyManager.setPolicy에서 갱신)
  sendAdminMessage('cmd.recording.set_policy', {
    policy: { ...policyModal.value.draft, updatedAt: Date.now() },
  })
}

const closeCastroom = async (index: number) => {
  const castroom = castroomList.value[index]
  const { castroomId } = castroom.castingSource
  const websocketUrl = dronecastStore.websocketUrl

  if (!dronecastClient) {
    dronecastClient = new DronecastClient('caster')
    dronecastClient.connect(websocketUrl)
  }

  // BL-611: admin이 종료한 경우 viewer에게 사유 전달
  dronecastClient.closeCastroom({ gcs: castroomId.gcs, drone: castroomId.drone, camera: castroomId.camera } as any, 'admin_closed')

  setTimeout(async () => {
    await dronecastStore.fetchAllCastrooms()
  }, 500)
}

/** Admin WS 연결 — stats + 실시간 목록 수신 */
function connectAdminWs() {
  const wsProto = location.protocol === 'https:' ? 'wss' : 'ws'
  // 빌드타임 env 가 아니라 **로그인한 사용자**의 자격증명을 쓴다.
  const { userEmail, apiKey } = getStoredAuth()
  const url = `${wsProto}://${location.host}/ws/admin?userEmail=${encodeURIComponent(
    userEmail
  )}&apiKey=${encodeURIComponent(apiKey)}`

  adminWs = new WebSocket(url)

  adminWs.onopen = () => {
    // 활성 녹화 목록 초기 동기화 — reload 시 누락 방지
    sendAdminMessage('cmd.recording.list', {})
  }

  adminWs.onmessage = (event) => {
    try {
      const msg = JSON.parse(event.data)
      const { type, payload } = msg

      if (type === 'castroom_stats_updated' && payload?.castroomId && payload?.stats?.caster) {
        const id = payload.castroomId
        const key = `${id.gcs}:${id.drone}:${id.camera}`
        castroomStats.value = new Map(castroomStats.value.set(key, payload.stats.caster))
      } else if (type === 'castroom_list_updated' && payload?.castRooms) {
        // 실시간 캐스트룸 목록 갱신
        dronecastStore.castroomList.splice(0)
        for (const item of payload.castRooms) {
          dronecastStore.castroomList.push({
            status: item.status,
            castingSource: {
              castroomId: { gcs: item.castroomId.gcs, drone: item.castroomId.drone, camera: item.castroomId.camera },
              droneName: item.castroomId.drone,
              cameraName: item.castroomId.camera,
            },
            rtspUrl: item.rtspUrl ?? null,
            viewerCount: item.numberOfViewers,
            mediaServerType: item.mediaServerType,
            casterLinkStatus: item.casterLinkStatus,
            sourceType: item.sourceType,
            thumbnailUrl: item.thumbnailUrl ?? null,
            thumbnailUpdatedAt: item.thumbnailUpdatedAt,
          })
        }
      } else if (type === 'castroom_thumbnail_updated' && payload?.castroomId) {
        // 썸네일 1건 갱신 — 해당 캐스트룸의 URL/타임스탬프만 교체 (이미지 즉시 새로고침)
        const id = payload.castroomId
        const room = dronecastStore.castroomList.find(c =>
          c.castingSource.castroomId.gcs === id.gcs &&
          c.castingSource.castroomId.drone === id.drone &&
          c.castingSource.castroomId.camera === id.camera)
        if (room) {
          room.thumbnailUrl = payload.thumbnailUrl
          room.thumbnailUpdatedAt = payload.thumbnailUpdatedAt
        }
      } else if (type === 'res.recording.start') {
        // 응답 — pendingRecordings에서 castroomKey 매칭
        if (payload?.result === 'success' && payload.data) {
          const id = payload.data.castroomId
          const key = `${id.gcs}:${id.drone}:${id.camera}`
          activeRecordings.value = new Map(activeRecordings.value.set(key, {
            recordingId: payload.data.recordingId,
            status: 'recording',
            startedAt: payload.data.startedAt,
          }))
        } else {
          errorMessage.value = `녹화 시작 실패: ${payload?.error || '알 수 없는 오류'}${payload?.errorCode ? ` (${payload.errorCode})` : ''}`
          setTimeout(() => { errorMessage.value = '' }, 5000)
        }
      } else if (type === 'res.recording.list') {
        // reload 후 활성 녹화 복원
        if (payload?.result === 'success' && Array.isArray(payload.data?.recordings)) {
          const m = new Map<string, ActiveRecordingState>()
          for (const r of payload.data.recordings) {
            const id = r.castroomId
            const key = `${id.gcs}:${id.drone}:${id.camera}`
            m.set(key, {
              recordingId: r.recordingId,
              status: r.status,
              startedAt: r.startedAt,
            })
          }
          activeRecordings.value = m
        }
      } else if (type === 'res.recording.stop') {
        if (payload?.result !== 'success') {
          errorMessage.value = `녹화 중지 실패: ${payload?.error || '알 수 없는 오류'}${payload?.errorCode ? ` (${payload.errorCode})` : ''}`
          setTimeout(() => { errorMessage.value = '' }, 5000)
        }
        // 성공 시는 evt.recording.completed에서 정리
      } else if (type === 'evt.recording.status_changed' && payload) {
        const id = payload.castroomId
        const key = `${id.gcs}:${id.drone}:${id.camera}`
        const existing = activeRecordings.value.get(key)
        const next: ActiveRecordingState = {
          recordingId: payload.recordingId,
          status: payload.status,
          startedAt: existing?.startedAt || Date.now(),
        }
        activeRecordings.value = new Map(activeRecordings.value.set(key, next))
      } else if (type === 'evt.recording.completed' && payload?.recording) {
        const id = payload.recording.castroomId
        const key = `${id.gcs}:${id.drone}:${id.camera}`
        const m = new Map(activeRecordings.value)
        m.delete(key)
        activeRecordings.value = m
      } else if (type === 'evt.recording.triggered' && payload) {
        // 자동 트리거 발동 — 토스트 알림
        const id = payload.castroomId
        errorMessage.value = `자동 녹화 시작: ${id.drone}/${id.camera} (${payload.trigger})`
        setTimeout(() => { errorMessage.value = '' }, 3000)
      } else if (type === 'res.recording.get_policy' && payload?.result === 'success') {
        const policy = payload.data?.policy as ClientRecordingPolicy | null
        if (policyModal.value.open && policyModal.value.castroom) {
          // 모달이 열려 있고 매칭 캐스트룸이면 draft 설정
          const k = castroomKey(policyModal.value.castroom)
          const reqId = policyModal.value.castroom.castingSource.castroomId
          if (policy && policy.castroomId.gcs === reqId.gcs && policy.castroomId.drone === reqId.drone && policy.castroomId.camera === reqId.camera) {
            policyModal.value.draft = { ...policy }
            recordingPolicies.value = new Map(recordingPolicies.value.set(k, policy))
          } else if (!policy) {
            recordingPolicies.value = new Map(recordingPolicies.value.set(k, null))
          }
        } else if (policy) {
          const k = `${policy.castroomId.gcs}:${policy.castroomId.drone}:${policy.castroomId.camera}`
          recordingPolicies.value = new Map(recordingPolicies.value.set(k, policy))
        }
      } else if (type === 'res.recording.set_policy') {
        if (payload?.result === 'success' && payload.data?.policy) {
          const p = payload.data.policy as ClientRecordingPolicy
          const k = `${p.castroomId.gcs}:${p.castroomId.drone}:${p.castroomId.camera}`
          recordingPolicies.value = new Map(recordingPolicies.value.set(k, p))
          if (policyModal.value.open) closePolicyModal()
        } else {
          policyModal.value.error = `정책 저장 실패: ${payload?.error || '알 수 없는 오류'}${payload?.errorCode ? ` (${payload.errorCode})` : ''}`
          policyModal.value.busy = false
        }
      }
    } catch {}
  }

  adminWs.onclose = () => {
    // 자동 재연결 (5초 후)
    setTimeout(() => {
      if (adminWs?.readyState !== WebSocket.OPEN) connectAdminWs()
    }, 5000)
  }
}

onMounted( async () => {
  await dronecastStore.fetchAllCastrooms()

  // Caster 조작용 클라이언트 (closeCastroom 등)
  dronecastClient = new DronecastClient('caster')
  dronecastClient.connect(dronecastStore.websocketUrl)

  // Admin 전용 WS 연결
  connectAdminWs()
})

onUnmounted(() => {
  if (dronecastClient) {
    dronecastClient.close()
  }
  if (adminWs) {
    adminWs.onclose = null  // 자동 재연결 방지
    adminWs.close()
    adminWs = null
  }
})

</script>

<template>
  <div class="publisher-container">
    <div v-if="errorMessage" class="alert alert-danger">
      {{ errorMessage }}
    </div>

    <div class="form-group horizontal">
      <button class="btn btn-primary" @click="onClickFetchDronecastList">전체 조회</button>
    </div>

    <table class="data-table">
      <thead>
        <tr>
          <th>썸네일</th>
          <th>상태</th>
          <th>링크</th>
          <th>출처</th>
          <th>미디어</th>
          <th>GCS</th>
          <th>드론</th>
          <th>카메라</th>
          <th>시청자</th>
          <th>미디어 품질</th>
          <th>녹화</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(castroom, index) in castroomList" :key="index">
          <!-- 플레이어 비활성: 기존 1줄 테이블 행 -->
          <tr v-if="!openPlayers.has(index)">
            <td class="thumb-cell">
              <img v-if="thumbUrl(castroom)" :src="thumbUrl(castroom)!" class="thumb-img" alt="썸네일"
                :title="'마지막 캡쳐: ' + new Date(castroom.thumbnailUpdatedAt!).toLocaleTimeString()" />
              <span v-else class="thumb-empty">—</span>
            </td>
            <td><span class="status-badge">{{ castroom.status }}</span></td>
            <td>
              <span class="link-indicator" :title="'Caster↔Server: ' + linkStatusLabel(castroom.casterLinkStatus)">
                <span class="link-dot" :style="{ background: linkStatusColor(castroom.casterLinkStatus) }"></span>
                <span class="link-arrow">→</span>
                <span class="link-dot" :style="{ background: castroom.status === 'casting' ? '#28a745' : '#6c757d' }"></span>
                <span class="link-arrow">→</span>
                <span class="link-dot" :style="{ background: '#6c757d' }"></span>
              </span>
            </td>
            <td :title="castroom.rtspUrl ? (sourceTypeTitle(castroom.sourceType) + '\n' + castroom.rtspUrl) : sourceTypeTitle(castroom.sourceType)">{{ sourceTypeLabel(castroom.sourceType, castroom.rtspUrl) }}</td>
            <td>{{ castroom.mediaServerType || '-' }}</td>
            <td>{{ castroom.castingSource.castroomId.gcs }}</td>
            <td>{{ castroom.castingSource.droneName }}</td>
            <td>{{ castroom.castingSource.cameraName }}</td>
            <td>{{ castroom.viewerCount }}</td>
            <td class="stats-cell">
              <template v-if="getStats(castroom)">
                <span v-for="m in primaryMetrics" :key="m.key" class="stat-badge" :title="metricTooltip(m, castroom.sourceType)">
                  <span class="stat-label">{{ m.label }}</span>
                  <span class="stat-value" :style="{ color: statsColor(renderMetricLevel(m, getStats(castroom)!, castroom.sourceType)) }">
                    {{ renderMetric(m, getStats(castroom)!, castroom.sourceType) }}
                  </span>
                </span>
                <button class="btn-stats-toggle" @click="toggleStats(castroomKey(castroom))">
                  {{ expandedStats.has(castroomKey(castroom)) ? '▲' : '▼' }}
                </button>
              </template>
              <span v-else class="stat-empty">-</span>
            </td>
            <td>
              <button v-if="!isRecording(castroom)"
                class="btn btn-sm btn-secondary"
                :disabled="castroom.status !== 'casting'"
                @click="onClickStartRecording(castroom)">● 녹화</button>
              <button v-else
                class="btn btn-sm btn-danger"
                :title="`녹화 중 (${recordingState(castroom)?.recordingId.slice(0, 8)})`"
                @click="onClickStopRecording(castroom)">■ 중지</button>
              <button class="btn btn-sm btn-outline"
                :title="`녹화 정책: ${policyLabel(castroom) || '미조회'}`"
                @click="openPolicyModal(castroom)">⚙</button>
            </td>
            <td>
              <button class="btn btn-sm btn-outline" @click="togglePlayer(index)">미리보기</button>
              <button class="btn btn-sm btn-outline" @click="openViewerPopup(index)">뷰어</button>
              <button class="btn btn-sm btn-danger" @click="closeCastroom(index)">종료</button>
            </td>
          </tr>
          <!-- 상세 통계 확장 행 (플레이어 비활성 상태에서만) -->
          <tr v-if="!openPlayers.has(index) && expandedStats.has(castroomKey(castroom)) && getStats(castroom)" class="stats-detail-row">
            <td :colspan="12" class="stats-detail-cell">
              <span v-for="m in detailMetrics" :key="m.key" class="stat-badge" :title="metricTooltip(m, castroom.sourceType)">
                <span class="stat-label">{{ m.label }}</span>
                <span class="stat-value" :style="{ color: statsColor(renderMetricLevel(m, getStats(castroom)!, castroom.sourceType)) }">
                  {{ renderMetric(m, getStats(castroom)!, castroom.sourceType) }}
                </span>
              </span>
            </td>
          </tr>
          <!-- 플레이어 활성: [정보 | 플레이어] 레이아웃 -->
          <tr v-if="openPlayers.has(index)" class="player-row">
            <td :colspan="12" class="player-cell">
              <div class="player-layout">
                <div class="player-info">
                  <div class="info-row">
                    <span class="info-item">
                      <span class="info-key">상태</span>
                      <span class="status-badge">{{ castroom.status }}</span>
                    </span>
                    <span class="info-item">
                      <span class="info-key">링크</span>
                      <span class="link-indicator-labeled">
                        <span class="link-node">
                          <span class="link-dot" :style="{ background: linkStatusColor(casterLinkStates.get(index) || castroom.casterLinkStatus) }"></span>
                          <span class="link-node-label">Caster</span>
                        </span>
                        <span class="link-arrow">→</span>
                        <span class="link-node">
                          <span class="link-dot" :style="{ background: castroom.status === 'casting' ? '#28a745' : '#6c757d' }"></span>
                          <span class="link-node-label">Server</span>
                        </span>
                        <span class="link-arrow">→</span>
                        <span class="link-node">
                          <span class="link-dot" :style="{ background: linkStatusColor(playerLinkStatus(index)) }"></span>
                          <span class="link-node-label">Player</span>
                        </span>
                      </span>
                    </span>
                    <span class="info-item">
                      <span class="info-key">출처</span>
                      <span class="info-val" :title="sourceTypeTitle(castroom.sourceType)">{{ sourceTypeLabel(castroom.sourceType, castroom.rtspUrl) }}</span>
                    </span>
                    <span class="info-item">
                      <span class="info-key">미디어</span>
                      <span class="info-val">{{ castroom.mediaServerType || '-' }}</span>
                    </span>
                  </div>
                  <div class="info-row">
                    <span class="info-item">
                      <span class="info-key">ID</span>
                      <span class="info-val">{{ castroom.castingSource.castroomId.gcs }} / {{ castroom.castingSource.droneName }} / {{ castroom.castingSource.cameraName }}</span>
                    </span>
                    <span class="info-item">
                      <span class="info-key">시청자</span>
                      <span class="info-val">{{ castroom.viewerCount }}</span>
                    </span>
                  </div>
                  <!-- 미디어 품질 (서버 push stats) -->
                  <div class="info-row stats-row" v-if="getStats(castroom)">
                    <span v-for="m in primaryMetrics" :key="m.key" class="stat-badge" :title="metricTooltip(m, castroom.sourceType)">
                      <span class="stat-label">{{ m.label }}</span>
                      <span class="stat-value" :style="{ color: statsColor(renderMetricLevel(m, getStats(castroom)!, castroom.sourceType)) }">
                        {{ renderMetric(m, getStats(castroom)!, castroom.sourceType) }}
                      </span>
                    </span>
                    <button class="btn-stats-toggle" @click="toggleStats(castroomKey(castroom))">
                      {{ expandedStats.has(castroomKey(castroom)) ? '▲' : '▼' }}
                    </button>
                  </div>
                  <div class="info-row stats-row" v-if="expandedStats.has(castroomKey(castroom)) && getStats(castroom)">
                    <span v-for="m in detailMetrics" :key="m.key" class="stat-badge" :title="metricTooltip(m, castroom.sourceType)">
                      <span class="stat-label">{{ m.label }}</span>
                      <span class="stat-value" :style="{ color: statsColor(renderMetricLevel(m, getStats(castroom)!, castroom.sourceType)) }">
                        {{ renderMetric(m, getStats(castroom)!, castroom.sourceType) }}
                      </span>
                    </span>
                  </div>
                  <div class="info-row actions">
                    <button v-if="!isRecording(castroom)"
                      class="btn btn-sm btn-secondary"
                      :disabled="castroom.status !== 'casting'"
                      @click="onClickStartRecording(castroom)">● 녹화</button>
                    <button v-else
                      class="btn btn-sm btn-danger"
                      :title="`녹화 중 (${recordingState(castroom)?.recordingId.slice(0, 8)})`"
                      @click="onClickStopRecording(castroom)">■ 중지</button>
                    <button class="btn btn-sm btn-danger" @click="closeCastroom(index)">종료</button>
                    <button class="btn btn-sm btn-outline" @click="togglePlayer(index)">미리보기 종료</button>
                    <button class="btn btn-sm btn-outline" @click="openViewerPopup(index)">뷰어</button>
                    <button v-if="isPlayerRetryable(index)" class="btn btn-sm btn-primary" @click="retryPlayer(index)">재시도</button>
                  </div>
                </div>
                <div class="mini-player-wrapper">
                  <DronecastPlayer
                    :ref="(el: any) => setPlayerRef(index, el)"
                    :castroomId="castroom.castingSource.castroomId"
                    :wsUrl="dronecastStore.websocketUrl"
                    :autoPlay="true"
                    :compact="true"
                    @onConnectionStateChanged="(state: ConnectionState) => onPlayerStateChanged(index, state)"
                    @onCasterLinkStatusChanged="(status: string) => onCasterLinkStatusChanged(index, status)"
                  />
                </div>
              </div>
            </td>
          </tr>
        </template>
        <tr v-if="castroomList.length === 0">
          <td colspan="12" style="text-align:center;color:#999;padding:24px">캐스트룸이 없습니다.</td>
        </tr>
      </tbody>
    </table>

    <!-- 녹화 정책 모달 (BL-637) -->
    <div v-if="policyModal.open && policyModal.draft" class="policy-modal-backdrop" @click.self="closePolicyModal">
      <div class="policy-modal">
        <div class="policy-modal-header">
          <h3>녹화 정책 설정</h3>
          <button class="btn-close" @click="closePolicyModal">✕</button>
        </div>
        <div class="policy-modal-body">
          <p class="policy-target">
            {{ policyModal.castroom.castingSource.castroomId.gcs }} /
            {{ policyModal.castroom.castingSource.castroomId.drone }} /
            {{ policyModal.castroom.castingSource.castroomId.camera }}
          </p>

          <div v-if="policyModal.error" class="alert alert-danger">{{ policyModal.error }}</div>

          <div class="policy-form-row">
            <label>트리거</label>
            <div class="trigger-options">
              <label class="radio-row">
                <input type="radio" v-model="policyModal.draft.trigger" value="manual" />
                <span>수동 (기본) — 명령으로만 시작/중지</span>
              </label>
              <label class="radio-row">
                <input type="radio" v-model="policyModal.draft.trigger" value="on_casting_start" />
                <span>송출 시작 시 — 캐스트룸이 송출 상태가 되면 자동 시작</span>
              </label>
              <label class="radio-row">
                <input type="radio" v-model="policyModal.draft.trigger" value="on_first_viewer" />
                <span>첫 시청자 시 — 시청자 0→1 시 시작, 1→0 시 자동 중지</span>
              </label>
            </div>
          </div>

          <div class="policy-form-row">
            <label>활성화</label>
            <label class="switch-row">
              <input type="checkbox" v-model="policyModal.draft.enabled" />
              <span>{{ policyModal.draft.enabled ? '활성' : '비활성' }} (비활성 시 자동 트리거 발동 안 함)</span>
            </label>
          </div>

          <div class="policy-form-row policy-format-row">
            <label>포맷</label>
            <span class="readonly-val">webm (1차 고정)</span>
          </div>
        </div>
        <div class="policy-modal-footer">
          <button class="btn btn-outline" @click="closePolicyModal" :disabled="policyModal.busy">취소</button>
          <button class="btn btn-primary" @click="savePolicyModal" :disabled="policyModal.busy">
            {{ policyModal.busy ? '저장 중...' : '저장' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.player-cell {
  padding: 6px 8px !important;
}

.player-layout {
  display: flex;
  gap: 10px;
  align-items: center;
}

.player-info {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.info-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.info-key {
  color: #999;
  font-size: 0.75em;
}

.info-val {
  color: inherit;
  font-size: 0.85em;
}

.info-row.actions {
  gap: 4px;
}

.mini-player-wrapper {
  width: 160px;
  min-width: 160px;
  height: 90px;
  background: #000;
  border-radius: 3px;
  overflow: hidden;
  margin-left: auto;
  flex-shrink: 0;
}

.mini-player-wrapper :deep(video) {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.link-indicator,
.link-indicator-labeled {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.link-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.link-arrow {
  color: #555;
  font-size: 0.75em;
}

.link-node {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
}

.link-node-label {
  font-size: 0.65em;
  color: #888;
}

.stats-cell {
  white-space: nowrap;
}

.stat-badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-right: 6px;
  font-size: 0.75em;
}

.stat-label {
  color: #888;
}

.stat-value {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.stat-empty {
  color: #555;
  font-size: 0.75em;
}

.btn-stats-toggle {
  background: none;
  border: none;
  color: #888;
  cursor: pointer;
  font-size: 0.7em;
  padding: 0 2px;
  vertical-align: middle;
}

.btn-stats-toggle:hover {
  color: #ccc;
}

.stats-detail-row .stats-detail-cell {
  padding: 4px 8px !important;
  background: rgba(255, 255, 255, 0.03);
  border-top: none;
}

.thumb-cell {
  width: 96px;
  padding: 4px !important;
}

.thumb-img {
  width: 88px;
  height: 50px;
  object-fit: cover;
  background: #000;
  border-radius: 3px;
  display: block;
}

.thumb-empty {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 88px;
  height: 50px;
  background: #1a1a1a;
  border-radius: 3px;
  color: #555;
  font-size: 0.8em;
}

.btn-outline {
  border: 1px solid #666;
  background: transparent;
  color: #ccc;
}
.btn-outline:hover {
  background: #333;
}

/* 녹화 정책 모달 (BL-637) */
.policy-modal-backdrop {
  position: fixed; inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex; align-items: center; justify-content: center;
  z-index: 2000;
}
.policy-modal {
  background: #2a2a2a;
  color: #eee;
  border-radius: 8px;
  width: min(560px, 90vw);
  display: flex; flex-direction: column;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}
.policy-modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #444;
}
.policy-modal-header h3 { margin: 0; font-size: 1.05rem; }
.btn-close { background: transparent; border: none; color: #aaa; font-size: 1.1rem; cursor: pointer; }
.policy-modal-body { padding: 16px; }
.policy-target { color: #aaa; font-size: 0.9rem; margin: 0 0 12px; }
.alert.alert-danger { background: #5a2a2a; color: #f4c2c2; padding: 8px 12px; border-radius: 4px; margin-bottom: 12px; font-size: 0.9rem; }

.policy-form-row { display: flex; gap: 12px; margin-bottom: 12px; }
.policy-form-row > label:first-child {
  flex: 0 0 80px; font-weight: 600; padding-top: 4px; color: #ccc;
}
.trigger-options { display: flex; flex-direction: column; gap: 8px; }
.radio-row, .switch-row { display: flex; align-items: flex-start; gap: 8px; cursor: pointer; }
.radio-row input, .switch-row input { margin-top: 3px; }
.radio-row span, .switch-row span { font-size: 0.92rem; line-height: 1.35; }
.readonly-val { color: #999; padding-top: 4px; }

.policy-modal-footer {
  padding: 12px 16px;
  border-top: 1px solid #444;
  display: flex; justify-content: flex-end; gap: 8px;
}
.policy-modal-footer .btn-primary { background: #0d6efd; border: none; color: white; padding: 6px 14px; border-radius: 4px; cursor: pointer; }
.policy-modal-footer .btn-primary:disabled { background: #444; cursor: not-allowed; }
.policy-modal-footer .btn-outline { padding: 6px 14px; }
</style>